'use client';

import Image from 'next/image';
import {Icon} from '@udemy-v2/icons';
import {BlockList} from '@udemy-v2/react-core-components';
import {getUdemyLogos} from '@udemy-v2/react-brand-components';
import {useFormatNumber, useI18n} from '@udemy-v2/i18n';

import styles from './teams-purchase-content.module.scss';
import {useOrganizationStats} from '@udemy-v2/site-stats';
import {getTeamPlanSeatCount, useTeamSubscriptionPlan} from '@udemy-v2/subscription-graphql';
import {UFBNoticeButton} from '@udemy-v2/organization-growth';
import {useUbCtaParams} from '@/features/course-landing-page/hooks/use-ub-cta-href';
import classNames from 'classnames';

export const TeamsPurchaseContent = ({
    premiumCourseNotice,
}: {
    premiumCourseNotice?: React.ReactNode;
}) => {
    const {gettext, interpolate, locale} = useI18n();
    const {formatNumber} = useFormatNumber();
    const {numCoursesRounded} = useOrganizationStats();
    const formattedNumCoursesRounded = formatNumber(numCoursesRounded);
    const udemyLogos = getUdemyLogos();
    const {ubHref} = useUbCtaParams();
    const teamSubscriptionPlan = useTeamSubscriptionPlan();
    const teamPlanMinimumSeatCount = getTeamPlanSeatCount(teamSubscriptionPlan);

    return (
        <div className={styles.container}>
            {premiumCourseNotice && premiumCourseNotice}
            <div className={styles.logo}>
                <Image src={udemyLogos.ubLogoUrl} width={180} height={40} alt="Udemy Business" />
            </div>

            <p className={classNames('ud-text-sm', styles.description)}>
                {interpolate(
                    gettext(
                        'Get your team access to %(courseCount)s+ top Udemy courses anytime, anywhere.',
                    ),
                    {courseCount: formattedNumCoursesRounded},
                    true,
                )}
            </p>

            <UFBNoticeButton
                className={styles.button}
                href={ubHref}
                size={'large'}
                eventData={{
                    locale,
                    placement: 'TeamsPurchaseContent',
                    url: ubHref,
                }}
            />

            <BlockList className={styles.list} size="small" padding="tight">
                <BlockList.Item
                    icon={<Icon glyph="tick" label={false} className={styles['checkbox-icon']} />}
                >
                    {interpolate(
                        gettext('For teams of %(minUsers)s or more users'),
                        {
                            minUsers: teamPlanMinimumSeatCount ?? '2',
                        },
                        true,
                    )}
                </BlockList.Item>
                <BlockList.Item
                    icon={<Icon glyph="tick" label={false} className={styles['checkbox-icon']} />}
                >
                    {interpolate(
                        gettext('%(courseCount)s+ fresh & in-demand courses'),
                        {
                            courseCount: formattedNumCoursesRounded,
                        },
                        true,
                    )}
                </BlockList.Item>
                <BlockList.Item
                    icon={<Icon glyph="tick" label={false} className={styles['checkbox-icon']} />}
                >
                    {gettext('Learning Engagement tools')}
                </BlockList.Item>
                <BlockList.Item
                    icon={<Icon glyph="tick" label={false} className={styles['checkbox-icon']} />}
                >
                    {gettext('SSO and LMS Integrations')}
                </BlockList.Item>
            </BlockList>
        </div>
    );
};
